import {
  useGetTrunkGatewayDidsQuery,
  useUpdateTrunkGatewayDidsMutation,
  useDeleteTrunkGatewayDidsMutation,
  handleUpdateResponse,
  handleDeleteResponse,
} from "./api";
import { useParams } from "react-router-dom";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import schema from "./schema";
import { useTable } from "@/react/components/table/useTable";
import columnDefinition from "./columnDefinition";
import React, { useCallback, useMemo, useState } from "react";
import TableComponent from "@/react/components/table/TableComponent";
import { FlexButton } from "@/react/components/table/styled";
import { StyledPlusCircleIcon } from "@/react/components/styled/StyledIcons";
import Modal, { ModalHeader } from "@8x8/oxygen-modal";
import { BulkAddDidsForm } from "@/react/features/support-tools/siptrunk/dids/BulkAddDidsForm";
import { useGatewayDidsMutation } from "@/react/features/support-tools/siptrunk/dids/useGatewayDidsMutation";
import { filterTableMutations } from "@/react/components/table/filterTableMutations";

const columns = columnDefinition();

export const GatewayDidsTable = () => {
  const { trunkId } = useParams();
  const {
    data: trunkGatewayDids,
    isLoading: isLoadingDids,
    error,
    refetch,
  } = useGetTrunkGatewayDidsQuery({ trunkId });

  const addresses = useMemo(() => {
    const addressMap = new Map();

    (trunkGatewayDids || []).forEach((did) => {
      const id = did.addressId;
      if (!addressMap.has(id)) {
        addressMap.set(id, { freeform: did.address?.freeForm, id });
      }
    });

    return Array.from(addressMap.values());
  }, [trunkGatewayDids]);

  const [updateDids, { isLoading: isUpdating }] =
    useUpdateTrunkGatewayDidsMutation();
  const [deleteDids, { isLoading: isDeleting }] =
    useDeleteTrunkGatewayDidsMutation();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  useErrorNotification(error);

  const idKey = schema.fields.didId.spec.key;

  const { table, filters, state, mutations } = useTable(
    trunkGatewayDids,
    columns,
    schema,
    idKey
  );

  const {
    data,
    rowsToUpdate,
    rowsToDelete,
    resetData,

    setData,
    setRowsToUpdate,
    setRowsToDelete,
  } = mutations;

  const onSave = useGatewayDidsMutation({
    rowsToUpdate,
    rowsToDelete,
    data,
    updateDids,
    deleteDids,
    setRowsToUpdate,
    setRowsToDelete,
    setData,
    handleUpdateResponse,
    handleDeleteResponse,
    errorNotification,
    trunkId,
    idKey,
  });

  const finalMutation = filterTableMutations(mutations, {
    update: true,
    delete: true,
  });

  const actionsSlot = useCallback(
    () => (
      <FlexButton variant="tertiary" onClick={() => setIsAddModalOpen(true)}>
        <StyledPlusCircleIcon />
        <span>Add</span>
      </FlexButton>
    ),
    [setIsAddModalOpen]
  );

  const isDirty = rowsToUpdate.size > 0 || rowsToDelete.size > 0;

  const isLoading = isLoadingDids || isDeleting || isUpdating;

  return (
    <>
      <TableComponent
        actionsSlot={actionsSlot}
        title="Gateway DIDs"
        schema={schema}
        isDirty={isDirty}
        isLoading={isLoading}
        onSave={onSave}
        onCancel={resetData}
        onRefresh={refetch}
        table={table}
        filters={filters}
        mutations={finalMutation}
        state={state}
      />
      {isAddModalOpen && (
        <Modal
          onClose={() => setIsAddModalOpen(false)}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
        >
          <ModalHeader
            title={"Bulk Add DIDs"}
            onClose={() => setIsAddModalOpen(false)}
          />
          <BulkAddDidsForm
            trunkId={trunkId}
            addresses={addresses}
            onClose={() => setIsAddModalOpen(false)}
          />
        </Modal>
      )}
    </>
  );
};
